import { useState, type ReactNode } from 'react';
import { Field, Select } from '@paige/ui';
import type { AccountHit, EnterpriseQuote, Interval, PlanKey } from './pb-types';
import { IntervalSelect } from './QuoteDialog';
import { day, quoteTerms } from './pb-words';

/** A plan a partner pays for: a package and interval, or Enterprise on one of the company's quotes. */
export interface PlanChoice {
  package: PlanKey;
  interval: Interval;
  quoteId: string | null;
}

const SOLD: PlanKey[] = ['starter', 'pro', 'business', 'enterprise'];
const WORD: Record<string, string> = {
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise (on a quote)',
};

/** The quotes a plan may take: the paying account's, still offered or in force, not past their day. */
export const takeable = (quotes: EnterpriseQuote[], payer: string | null, today: string) =>
  quotes.filter(
    (q) =>
      q.zohoAccountId === payer &&
      (q.state === 'offered' || q.state === 'accepted') &&
      q.validUntil >= today,
  );

/** Who pays and at what plan, with the quotes that plan may take and whether the choice is whole. */
export function usePayerPlan(all: EnterpriseQuote[], today: string) {
  const [payer, setPayer] = useState<AccountHit | null>(null);
  const [plan, setPlan] = useState<PlanChoice>({
    package: 'pro',
    interval: 'month',
    quoteId: null,
  });
  const quotes = takeable(all, payer?.zohoAccountId ?? null, today);
  const quote = quotes.find((q) => q.id === plan.quoteId) ?? null;
  const ready = payer !== null && (plan.package !== 'enterprise' || quote !== null);
  return { payer, setPayer, plan, setPlan, quotes, quote, ready };
}

/** Package, interval and, for Enterprise, the quote; Enterprise's interval is the quote's. */
export function PlanPicker(p: {
  value: PlanChoice;
  onChange: (v: PlanChoice) => void;
  quotes: EnterpriseQuote[];
}): ReactNode {
  const v = p.value;
  const pick = (id: string) => {
    const q = p.quotes.find((x) => x.id === id);
    p.onChange({ ...v, quoteId: q?.id ?? null, interval: q?.interval ?? v.interval });
  };
  return (
    <div className="pb-grid2">
      <Field label="Plan">
        {({ id }) => (
          <Select
            id={id}
            value={v.package}
            onChange={(e) =>
              p.onChange({ ...v, package: e.target.value as PlanKey, quoteId: null })
            }
          >
            {SOLD.map((k) => (
              <option key={k} value={k}>
                {WORD[k]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {v.package === 'enterprise' ? (
        <QuoteSelect quotes={p.quotes} value={v.quoteId} onChange={pick} />
      ) : (
        <IntervalSelect
          value={v.interval}
          onChange={(interval) => p.onChange({ ...v, interval })}
        />
      )}
    </div>
  );
}

function QuoteSelect(p: {
  quotes: EnterpriseQuote[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <Field
      label="Quote"
      hint={
        p.quotes.length === 0
          ? 'Record an Enterprise quote for this paying account first'
          : undefined
      }
    >
      {({ id, describedBy }) => (
        <Select
          id={id}
          aria-describedby={describedBy}
          value={p.value ?? ''}
          onChange={(e) => p.onChange(e.target.value)}
        >
          <option value="">Choose a quote</option>
          {p.quotes.map((q) => (
            <option key={q.id} value={q.id}>
              {(q.quoteRef ?? 'Quote') + ': ' + quoteTerms(q) + ', until ' + day(q.validUntil)}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}
